import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  ListTodo, 
  Plus,
  Filter,
  Search,
  MoreVertical,
  Calendar,
  Clock,
  CheckCircle2,
  Circle,
  ArrowRight,
  User,
  Loader2
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Avatar, AvatarFallback } from '../../components/ui/avatar';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { api } from '../../context/AuthContext';
import { toast } from 'sonner';

const EMPTY_BOARD = { todo: [], in_progress: [], review: [], done: [] };

function groupTasksByStatus(taskList) {
  const board = { todo: [], in_progress: [], review: [], done: [] };
  for (const task of taskList) {
    const col = board[task.status] ? task.status : 'todo';
    board[col].push(task);
  }
  return board;
}

const COLUMNS = [
  { id: 'todo', title: 'To Do', icon: Circle, color: 'slate' },
  { id: 'in_progress', title: 'In Progress', icon: Clock, color: 'blue' },
  { id: 'review', title: 'Review', icon: ArrowRight, color: 'amber' },
  { id: 'done', title: 'Done', icon: CheckCircle2, color: 'emerald' },
];

const TASK_TYPES = [
  { value: 'on_page_seo', label: 'On-Page SEO' },
  { value: 'off_page_seo', label: 'Off-Page SEO' },
  { value: 'technical_seo', label: 'Technical SEO' },
  { value: 'content_creation', label: 'Content Creation' },
  { value: 'content_review', label: 'Content Review' },
  { value: 'keyword_research', label: 'Keyword Research' },
  { value: 'backlink_outreach', label: 'Backlink Outreach' },
  { value: 'gbp_optimization', label: 'GBP Optimization' },
  { value: 'reporting', label: 'Reporting' },
  { value: 'other', label: 'Other' },
];

const INITIAL_FORM = {
  title: '',
  description: '',
  project_id: '',
  task_type: '',
  assigned_to: '',
  due_date: '',
};

function toDateInputValue(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toISOString().slice(0, 10);
}

export default function TaskBoard() {
  const [tasks, setTasks] = useState(EMPTY_BOARD);
  const [searchTerm, setSearchTerm] = useState('');
  const [draggedTask, setDraggedTask] = useState(null);
  const [loading, setLoading] = useState(true);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [detailsTask, setDetailsTask] = useState(null);
  const [editingTask, setEditingTask] = useState(null);
  const [formData, setFormData] = useState(INITIAL_FORM);
  const [editFormData, setEditFormData] = useState(INITIAL_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [projects, setProjects] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);

  const fetchTasks = async () => {
    try {
      const response = await api.get('/tasks');
      const taskList = response.data?.items || response.data || [];
      setTasks(groupTasksByStatus(taskList));
    } catch (error) {
      console.error('Failed to fetch tasks:', error);
      toast.error('Failed to load tasks');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTasks();
  }, []);

  useEffect(() => {
    if (!dialogOpen && !editDialogOpen && !detailsTask) return;
    const loadMeta = async () => {
      try {
        const [projRes, usersRes] = await Promise.allSettled([
          api.get('/projects'),
          api.get('/tasks/assignees'),
        ]);
        if (projRes.status === 'fulfilled') {
          const data = projRes.value.data;
          setProjects(Array.isArray(data) ? data : data?.items || []);
        }
        if (usersRes.status === 'fulfilled') {
          const data = usersRes.value.data;
          setTeamMembers(Array.isArray(data) ? data : data?.items || []);
        }
      } catch (_) { /* non-critical */ }
    };
    loadMeta();
  }, [dialogOpen, editDialogOpen, detailsTask]);

  const handleCreateTask = async () => {
    if (!formData.title.trim() || !formData.project_id || !formData.task_type) {
      toast.error('Please fill in title, project, and task type');
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        title: formData.title.trim(),
        project_id: formData.project_id,
        task_type: formData.task_type,
        description: formData.description.trim() || null,
        assigned_to: formData.assigned_to || null,
        due_date: formData.due_date ? `${formData.due_date}T00:00:00` : null,
      };
      await api.post('/tasks', payload);
      toast.success('Task created successfully');
      setDialogOpen(false);
      setFormData(INITIAL_FORM);
      await fetchTasks();
    } catch (error) {
      console.error('Failed to create task:', error);
      toast.error(error?.response?.data?.detail || 'Failed to create task');
    } finally {
      setSubmitting(false);
    }
  };

  const openEditTask = (task) => {
    setEditingTask(task);
    setEditFormData({
      title: task.title || '',
      description: task.description || '',
      project_id: task.project_id || '',
      task_type: task.task_type || '',
      assigned_to: task.assigned_to || '',
      due_date: toDateInputValue(task.due_date),
    });
    setEditDialogOpen(true);
  };

  const handleUpdateTask = async () => {
    if (!editingTask) return;
    if (!editFormData.title.trim() || !editFormData.task_type) {
      toast.error('Please fill in title and task type');
      return;
    }
    setSubmitting(true);
    try {
      await api.patch(`/tasks/${editingTask.task_id || editingTask.id}`, {
        title: editFormData.title.trim(),
        project_id: editFormData.project_id || null,
        task_type: editFormData.task_type,
        description: editFormData.description.trim() || null,
        assigned_to: editFormData.assigned_to || null,
        due_date: editFormData.due_date ? `${editFormData.due_date}T00:00:00` : null,
      });
      toast.success('Task updated successfully');
      setEditDialogOpen(false);
      setEditingTask(null);
      setEditFormData(INITIAL_FORM);
      await fetchTasks();
    } catch (error) {
      console.error('Failed to update task:', error);
      toast.error(error?.response?.data?.detail || 'Failed to update task');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteTask = async (taskId, columnId) => {
    try {
      await api.delete(`/tasks/${taskId}`);
      setTasks(prev => ({
        ...prev,
        [columnId]: prev[columnId].filter(t => (t.task_id || t.id) !== taskId),
      }));
      toast.success('Task deleted');
    } catch (error) {
      console.error('Failed to delete task:', error);
      toast.error(error?.response?.data?.detail || 'Failed to delete task');
    }
  };

  const getPriorityBadge = (priority) => {
    const priorities = {
      high: { label: 'High', color: 'bg-red-100 text-red-700' },
      medium: { label: 'Medium', color: 'bg-amber-100 text-amber-700' },
      low: { label: 'Low', color: 'bg-slate-100 text-slate-700' },
    };
    return priorities[priority] || priorities.medium;
  };

  const handleDragStart = (e, task, columnId) => {
    setDraggedTask({ task, columnId });
    if (e.dataTransfer) e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e, targetColumnId) => {
    e.preventDefault();
    if (!draggedTask) return;

    const { task, columnId: sourceColumnId } = draggedTask;
    
    if (sourceColumnId === targetColumnId) {
      setDraggedTask(null);
      return;
    }

    const taskId = task.task_id || task.id;

    setTasks(prev => {
      const newTasks = { ...prev };
      newTasks[sourceColumnId] = newTasks[sourceColumnId].filter(
        t => (t.task_id || t.id) !== taskId
      );
      newTasks[targetColumnId] = [...newTasks[targetColumnId], { ...task, status: targetColumnId }];
      return newTasks;
    });

    setDraggedTask(null);

    try {
      await api.put(`/tasks/${taskId}/status`, null, {
        params: { status: targetColumnId },
      });
      toast.success(`Task moved to ${COLUMNS.find(c => c.id === targetColumnId)?.title}`);
    } catch (error) {
      setTasks(prev => {
        const rollback = { ...prev };
        const movedTask = rollback[targetColumnId].find(
          t => (t.task_id || t.id) === taskId
        );
        if (movedTask) {
          rollback[targetColumnId] = rollback[targetColumnId].filter(
            t => (t.task_id || t.id) !== taskId
          );
          rollback[sourceColumnId] = [...rollback[sourceColumnId], { ...movedTask, status: sourceColumnId }];
        }
        return rollback;
      });
      toast.error('Failed to update task status');
    }
  };

  const filteredTasks = (columnId) => {
    return (tasks[columnId] || []).filter(task => {
      const title = (task.title || '').toLowerCase();
      const project = (task.project_id || task.project || '').toLowerCase();
      const term = searchTerm.toLowerCase();
      return title.includes(term) || project.includes(term);
    });
  };

  const totalTasks = Object.values(tasks).flat().length;
  const completedTasks = (tasks.done || []).length;
  const getAssigneeLabel = (assigneeId) => {
    if (!assigneeId) return 'Unassigned';
    const member = teamMembers.find(u => u.user_id === assigneeId);
    return member?.name || member?.email || assigneeId;
  };
  const getProjectLabel = (projectId) => {
    if (!projectId) return 'No project';
    const project = projects.find(p => p.project_id === projectId);
    return project?.name || project?.domain || projectId;
  };
  const formatDateTime = (value) => {
    if (!value) return null;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return null;
    return date.toLocaleString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: 'numeric', minute: '2-digit',
    });
  };
  const formatTaskType = (type) =>
    type ? type.replaceAll('_', ' ').replace(/\b\w/g, c => c.toUpperCase()) : '—';

  if (loading) {
    return (
      <div className="space-y-6" data-testid="task-board-loading">
        <div className="h-8 w-48 bg-slate-200 dark:bg-slate-700 rounded animate-pulse" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="space-y-3">
              <div className="h-6 w-24 bg-slate-200 dark:bg-slate-700 rounded animate-pulse" />
              <div className="space-y-3 min-h-[400px] p-2 rounded-xl bg-slate-100/50 dark:bg-slate-800/30">
                {[1, 2].map(j => (
                  <div key={j} className="h-32 bg-slate-200 dark:bg-slate-700 rounded-xl animate-pulse" />
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="task-board">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold mb-1">Task Board</h1>
          <p className="text-slate-600 dark:text-slate-400">
            {completedTasks} of {totalTasks} tasks completed
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search tasks..."
              className="pl-9 w-64"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              data-testid="task-search-input"
            />
          </div>
          <Button variant="outline" size="icon">
            <Filter className="w-4 h-4" />
          </Button>
          <Button className="gds-btn-primary" data-testid="create-task-btn" onClick={() => setDialogOpen(true)}>
            <Plus className="w-4 h-4 mr-2" />
            Add Task
          </Button>
        </div>
      </div>

      {/* Kanban Board */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {COLUMNS.map((column) => (
          <div
            key={column.id}
            className="flex flex-col"
            onDragOver={handleDragOver}
            onDrop={(e) => handleDrop(e, column.id)}
          >
            {/* Column Header */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <column.icon className={`w-5 h-5 text-${column.color}-500`} />
                <span className="font-heading font-semibold">{column.title}</span>
                <Badge variant="secondary" className="ml-1">
                  {filteredTasks(column.id).length}
                </Badge>
              </div>
              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setDialogOpen(true)}>
                <Plus className="w-4 h-4" />
              </Button>
            </div>

            {/* Tasks */}
            <div className="space-y-2 min-h-[120px] p-2 rounded-xl bg-slate-100/50 dark:bg-slate-800/30">
              {filteredTasks(column.id).map((task, i) => {
                const assignee = task.assigned_to || task.assignee || '';
                const assigneeLabel = getAssigneeLabel(assignee);
                const initials = assignee.split(' ').filter(Boolean).map(n => n[0]).join('').toUpperCase() || '?';
                const dueLabel = task.due_date
                  ? new Date(task.due_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                  : task.due || '';
                return (
                  <motion.div
                    key={task.task_id || task.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.2, delay: i * 0.05 }}
                    draggable
                    onDragStart={(e) => handleDragStart(e, task, column.id)}
                    className="cursor-grab active:cursor-grabbing"
                  >
                    <Card className="gds-card hover:shadow-md transition-shadow">
                      <CardContent className="p-3">
                        <div className="flex items-start justify-between gap-2 mb-1.5">
                          <Badge className={`${getPriorityBadge(task.priority).color} text-[10px] px-1.5 py-0`}>
                            {getPriorityBadge(task.priority).label}
                          </Badge>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" className="h-5 w-5 -mr-1 -mt-0.5 shrink-0">
                                <MoreVertical className="w-3.5 h-3.5" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => openEditTask(task)}>
                                Edit / Assign
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => setDetailsTask(task)}>
                                View Details
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                className="text-red-600"
                                onClick={() => handleDeleteTask(task.task_id || task.id, column.id)}
                              >
                                Delete
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>

                        <h3 className="text-sm font-medium leading-snug mb-1.5 line-clamp-2">{task.title}</h3>

                        <div className="flex items-center justify-between gap-2">
                          <div className="flex items-center gap-1.5 min-w-0">
                            <Avatar className="h-5 w-5">
                              <AvatarFallback className="text-[10px] bg-indigo-100 text-indigo-600">
                                {assignee ? initials : <User className="h-3 w-3" />}
                              </AvatarFallback>
                            </Avatar>
                            <span className="text-[11px] text-slate-500 truncate">{assigneeLabel}</span>
                          </div>
                          {dueLabel && (
                            <div className="flex items-center gap-1 text-[11px] text-slate-500 shrink-0">
                              <Calendar className="w-3 h-3" />
                              {dueLabel}
                            </div>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                );
              })}
              
              {filteredTasks(column.id).length === 0 && (
                <div className="flex items-center justify-center h-20 text-slate-400 text-sm">
                  No tasks
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Create Task Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-[520px]">
          <DialogHeader>
            <DialogTitle>Create New Task</DialogTitle>
            <DialogDescription>Add a task to the board. Fill in the details below.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-2">
            <div className="grid gap-2">
              <Label htmlFor="task-title">Title *</Label>
              <Input
                id="task-title"
                placeholder="e.g. Optimize homepage meta tags"
                value={formData.title}
                onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label>Project *</Label>
                <Select
                  value={formData.project_id}
                  onValueChange={(val) => setFormData(prev => ({ ...prev, project_id: val }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select project" />
                  </SelectTrigger>
                  <SelectContent>
                    {projects.map(p => (
                      <SelectItem key={p.project_id} value={p.project_id}>
                        {p.name || p.domain || p.project_id}
                      </SelectItem>
                    ))}
                    {projects.length === 0 && (
                      <SelectItem value="__none" disabled>No projects found</SelectItem>
                    )}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-2">
                <Label>Task Type *</Label>
                <Select
                  value={formData.task_type}
                  onValueChange={(val) => setFormData(prev => ({ ...prev, task_type: val }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    {TASK_TYPES.map(t => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label>Assign To</Label>
                <Select
                  value={formData.assigned_to}
                  onValueChange={(val) => setFormData(prev => ({ ...prev, assigned_to: val }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Unassigned" />
                  </SelectTrigger>
                  <SelectContent>
                    {teamMembers.map(u => (
                      <SelectItem key={u.user_id} value={u.user_id}>
                        {u.name || u.email}
                      </SelectItem>
                    ))}
                    {teamMembers.length === 0 && (
                      <SelectItem value="__none" disabled>No team members</SelectItem>
                    )}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-2">
                <Label htmlFor="task-due">Due Date</Label>
                <Input
                  id="task-due"
                  type="date"
                  value={formData.due_date}
                  onChange={(e) => setFormData(prev => ({ ...prev, due_date: e.target.value }))}
                />
              </div>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="task-desc">Description</Label>
              <Textarea
                id="task-desc"
                placeholder="Optional description..."
                rows={3}
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={submitting}>
              Cancel
            </Button>
            <Button className="gds-btn-primary" onClick={handleCreateTask} disabled={submitting}>
              {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Create Task
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit / Assign Task Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={(open) => {
        setEditDialogOpen(open);
        if (!open) {
          setEditingTask(null);
          setEditFormData(INITIAL_FORM);
        }
      }}>
        <DialogContent className="sm:max-w-[560px]">
          <DialogHeader>
            <DialogTitle>Edit Task</DialogTitle>
            <DialogDescription>Update the task details, owner, or due date.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-2">
            <div className="grid gap-2">
              <Label htmlFor="edit-task-title">Title *</Label>
              <Input
                id="edit-task-title"
                value={editFormData.title}
                onChange={(e) => setEditFormData(prev => ({ ...prev, title: e.target.value }))}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label>Project</Label>
                <Select
                  value={editFormData.project_id}
                  onValueChange={(val) => setEditFormData(prev => ({ ...prev, project_id: val }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select project" />
                  </SelectTrigger>
                  <SelectContent>
                    {projects.map(p => (
                      <SelectItem key={p.project_id} value={p.project_id}>
                        {p.name || p.domain || p.project_id}
                      </SelectItem>
                    ))}
                    {projects.length === 0 && (
                      <SelectItem value="__none" disabled>No projects found</SelectItem>
                    )}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-2">
                <Label>Task Type *</Label>
                <Select
                  value={editFormData.task_type}
                  onValueChange={(val) => setEditFormData(prev => ({ ...prev, task_type: val }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    {TASK_TYPES.map(t => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                    {editFormData.task_type && !TASK_TYPES.some(t => t.value === editFormData.task_type) && (
                      <SelectItem value={editFormData.task_type}>
                        {editFormData.task_type.replaceAll('_', ' ')}
                      </SelectItem>
                    )}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label>Assign To</Label>
                <Select
                  value={editFormData.assigned_to || '__unassigned'}
                  onValueChange={(val) => setEditFormData(prev => ({
                    ...prev,
                    assigned_to: val === '__unassigned' ? '' : val,
                  }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Unassigned" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__unassigned">Unassigned</SelectItem>
                    {teamMembers.map(u => (
                      <SelectItem key={u.user_id} value={u.user_id}>
                        {u.name || u.email}
                      </SelectItem>
                    ))}
                    {teamMembers.length === 0 && (
                      <SelectItem value="__none" disabled>No team members</SelectItem>
                    )}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-2">
                <Label htmlFor="edit-task-due">Due Date</Label>
                <Input
                  id="edit-task-due"
                  type="date"
                  value={editFormData.due_date}
                  onChange={(e) => setEditFormData(prev => ({ ...prev, due_date: e.target.value }))}
                />
              </div>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="edit-task-desc">Description</Label>
              <Textarea
                id="edit-task-desc"
                rows={5}
                value={editFormData.description}
                onChange={(e) => setEditFormData(prev => ({ ...prev, description: e.target.value }))}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)} disabled={submitting}>
              Cancel
            </Button>
            <Button className="gds-btn-primary" onClick={handleUpdateTask} disabled={submitting}>
              {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Task Details Dialog */}
      <Dialog open={Boolean(detailsTask)} onOpenChange={(open) => !open && setDetailsTask(null)}>
        <DialogContent className="sm:max-w-[560px]">
          <DialogHeader>
            <DialogTitle>{detailsTask?.title}</DialogTitle>
            <DialogDescription>
              {formatTaskType(detailsTask?.task_type) || 'Task details'}
            </DialogDescription>
          </DialogHeader>
          {detailsTask && (
            <div className="space-y-4">
              <div className="flex flex-wrap gap-2">
                <Badge variant="outline">
                  {COLUMNS.find(c => c.id === detailsTask.status)?.title || detailsTask.status || 'To Do'}
                </Badge>
                <Badge className={`${getPriorityBadge(detailsTask.priority).color}`}>
                  {getPriorityBadge(detailsTask.priority).label} priority
                </Badge>
              </div>

              <dl className="grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
                <div>
                  <dt className="text-xs font-medium uppercase tracking-wide text-slate-400 mb-0.5">Project</dt>
                  <dd className="text-slate-700 dark:text-slate-200">{getProjectLabel(detailsTask.project_id)}</dd>
                </div>
                <div>
                  <dt className="text-xs font-medium uppercase tracking-wide text-slate-400 mb-0.5">Task Type</dt>
                  <dd className="text-slate-700 dark:text-slate-200">{formatTaskType(detailsTask.task_type)}</dd>
                </div>
                <div>
                  <dt className="text-xs font-medium uppercase tracking-wide text-slate-400 mb-0.5">Assigned To</dt>
                  <dd className="text-slate-700 dark:text-slate-200">{getAssigneeLabel(detailsTask.assigned_to)}</dd>
                </div>
                <div>
                  <dt className="text-xs font-medium uppercase tracking-wide text-slate-400 mb-0.5">Due Date</dt>
                  <dd className="text-slate-700 dark:text-slate-200">
                    {detailsTask.due_date
                      ? new Date(detailsTask.due_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                      : 'Not set'}
                  </dd>
                </div>
                <div>
                  <dt className="text-xs font-medium uppercase tracking-wide text-slate-400 mb-0.5">Created By</dt>
                  <dd className="text-slate-700 dark:text-slate-200">{getAssigneeLabel(detailsTask.created_by)}</dd>
                </div>
                <div>
                  <dt className="text-xs font-medium uppercase tracking-wide text-slate-400 mb-0.5">Created</dt>
                  <dd className="text-slate-700 dark:text-slate-200">{formatDateTime(detailsTask.created_at) || '—'}</dd>
                </div>
                {detailsTask.updated_at && (
                  <div>
                    <dt className="text-xs font-medium uppercase tracking-wide text-slate-400 mb-0.5">Last Updated</dt>
                    <dd className="text-slate-700 dark:text-slate-200">{formatDateTime(detailsTask.updated_at) || '—'}</dd>
                  </div>
                )}
                {detailsTask.completed_at && (
                  <div>
                    <dt className="text-xs font-medium uppercase tracking-wide text-slate-400 mb-0.5">Completed</dt>
                    <dd className="text-slate-700 dark:text-slate-200">{formatDateTime(detailsTask.completed_at) || '—'}</dd>
                  </div>
                )}
              </dl>

              <div>
                <p className="text-sm font-medium text-slate-700 dark:text-slate-200 mb-2">
                  Description
                </p>
                <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm leading-6 text-slate-700 whitespace-pre-line dark:border-slate-700 dark:bg-slate-800/50 dark:text-slate-200">
                  {detailsTask.description || 'No description provided.'}
                </div>
              </div>
              {detailsTask.metadata?.target_url && (
                <div>
                  <p className="text-sm font-medium text-slate-700 dark:text-slate-200 mb-1">
                    Target URL
                  </p>
                  <a
                    href={detailsTask.metadata.target_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-indigo-600 hover:underline"
                  >
                    {detailsTask.metadata.target_url}
                  </a>
                </div>
              )}

              <DialogFooter>
                <Button variant="outline" onClick={() => setDetailsTask(null)}>
                  Close
                </Button>
                <Button
                  className="gds-btn-primary"
                  onClick={() => { const t = detailsTask; setDetailsTask(null); openEditTask(t); }}
                >
                  Edit / Assign
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
